import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useLayoutEffect, useState } from 'react';
import { offers } from '@/api/endpoints';
import type { Offer } from '@/types';
import { Icon } from './Icon';
import { TiptapEditor } from './TiptapEditor';

interface Props {
  open: boolean;
  offer: Offer | null;
  onClose: () => void;
  onSent: (updated: Offer) => void;
}

export function OfferEmailModal({ open, offer, onClose, onSent }: Props) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachPdf, setAttachPdf] = useState(true);
  const [attachDocx, setAttachDocx] = useState(false);
  const [attachmentName, setAttachmentName] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sentInfo, setSentInfo] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (!open || !offer) return;
    setErr(null); setSentInfo(null); setBusy(false);
    setLoading(true);
    offers.emailDefaults(offer.id).then(d => {
      resetDirty();
      setTo(d.to);
      setSubject(d.subject);
      setBodyHtml(d.body);
      setAttachmentName(d.attachmentName);
      setAttachPdf(true);
      setAttachDocx(false);
      setCc(''); setBcc('');
      setLoading(false);
    }).catch(e => { setErr(e?.message ?? String(e)); setLoading(false); });
  }, [open, offer]);

  const { requestClose, confirmation, dialogProps, resetDirty, markSaved } = useUnsavedChanges({
    value: { to, cc, bcc, subject, bodyHtml, attachPdf, attachDocx }, onClose, open, busy: busy || loading,
  });

  if (!open || !offer) return null;

  const parseList = (s: string) =>
    s.split(/[,;\s]+/).map(x => x.trim()).filter(Boolean);

  const send = (e: React.FormEvent) => {
    e.preventDefault();
    if (!to.trim()) { setErr('Adres odbiorcy jest wymagany'); return; }
    if (!subject.trim()) { setErr('Temat jest wymagany'); return; }
    setBusy(true); setErr(null); setSentInfo(null);
    offers.sendEmail(offer.id, {
      to: to.trim(),
      cc: cc.trim() ? parseList(cc) : undefined,
      bcc: bcc.trim() ? parseList(bcc) : undefined,
      subject: subject.trim(),
      bodyHtml,
      attachPdf,
      attachDocx,
    })
      .then(updated => {
        markSaved();
        setSentInfo('Wysłano. Status oferty: ' + updated.statusName);
        // Krótka chwila żeby user widział sukces, potem zamknij
        setTimeout(() => onSent(updated), 800);
      })
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onSubmit={send} onClick={e => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full max-w-[820px] max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-100">
          <div>
            <h2 className="text-[16px] font-semibold text-ink-900">Wyślij ofertę e-mailem</h2>
            <div className="text-[11px] text-ink-500 font-mono">{offer.number} · {offer.title}</div>
          </div>
          <button type="button" onClick={requestClose} className="btn btn-ghost btn-icon text-ink-400 hover:text-ink-700">
            <Icon name="x"/>
          </button>
        </div>

        {loading && <div className="p-6 text-ink-400 text-center">Przygotowywanie kompozycji…</div>}

        {!loading && (
          <div className="p-5 flex flex-col gap-3">
            <label className="field">
              <span className="field-label">Do *</span>
              <input value={to} onChange={e => setTo(e.target.value)} className="input" placeholder="adres@firma.pl" required/>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="field">
                <span className="field-label">DW (CC) — oddziel przecinkami</span>
                <input value={cc} onChange={e => setCc(e.target.value)} className="input" placeholder="opcjonalnie"/>
              </label>
              <label className="field">
                <span className="field-label">UDW (BCC)</span>
                <input value={bcc} onChange={e => setBcc(e.target.value)} className="input" placeholder="opcjonalnie"/>
              </label>
            </div>
            <label className="field">
              <span className="field-label">Temat *</span>
              <input value={subject} onChange={e => setSubject(e.target.value)} className="input" required/>
            </label>
            <div className="field">
              <span className="field-label">Treść wiadomości</span>
              <TiptapEditor value={bodyHtml} onChange={setBodyHtml} minHeight="200px"/>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="field-label">Załączniki</div>
              <label className="flex items-center gap-2 text-[13px] text-ink-700 cursor-pointer">
                <input type="checkbox" checked={attachPdf} onChange={e => setAttachPdf(e.target.checked)}/>
                <span>PDF: <span className="font-mono text-ink-900">{attachmentName}</span></span>
              </label>
              <label className="flex items-center gap-2 text-[13px] text-ink-700 cursor-pointer">
                <input type="checkbox" checked={attachDocx} onChange={e => setAttachDocx(e.target.checked)}/>
                <span>DOCX: <span className="font-mono text-ink-900">{attachmentName.replace(/\.pdf$/, '.docx')}</span> <span className="text-[11px] text-ink-400">(edytowalny w Word)</span></span>
              </label>
            </div>

            {err && <div className="text-red-500 text-[12px] p-2 bg-red-50 border border-red-200 rounded">Błąd: {err}</div>}
            {sentInfo && <div className="text-green-700 text-[12px] p-2 bg-green-50 border border-green-200 rounded">{sentInfo}</div>}

            <div className="text-[11px] text-ink-400 leading-relaxed bg-ink-50 p-3 rounded">
              Wysyłka korzysta z konfiguracji SMTP w <strong>Administracja → Konfiguracja systemu → MAIL</strong>.
              W trybie dev MailHog odbiera maile i pokazuje na <a className="text-water-600" href="http://localhost:8025" target="_blank" rel="noreferrer">localhost:8025</a>.
              Po wysyłce status oferty zmieni się automatycznie na "Wysłana" (jeśli był DRAFT).
            </div>
          </div>
        )}

        <div className="px-5 py-3 border-t border-ink-100 flex justify-end gap-2">
          <button type="button" onClick={requestClose} className="btn btn-secondary">Anuluj</button>
          <button type="submit" disabled={busy || loading} className="btn btn-primary">
            {busy ? 'Wysyłanie…' : 'Wyślij'}
          </button>
        </div>
      </form>
    </div>
  );
}
